import { useEffect, useState } from 'react';
import type { ApiClient, Systemrechte } from './api';

/**
 * Was die angemeldete Person darf (TD-WR1, GO main 25.10.2026).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WARUM
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Seit S12 (v0.52.33) nimmt der Server Aufträge, die ins Modell schreiben,
 * nur von ProjectAdmin und SystemAdmin an; alle anderen bekommen 403. Die
 * Fachseiten zeigten die Knöpfe „Ins Modell schreiben" trotzdem jedem - wer
 * ohne Rolle drückte, las erst danach die Absage. Jetzt fragt die Oberfläche
 * einmal, ob die Person schreiben darf, und blendet die Knöpfe sonst aus.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DIE SPERRE BLEIBT DER SERVER
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Hier wird nur ausgeblendet, nichts freigegeben. Deshalb gilt im Zweifel
 * „darf nicht": solange die Antwort fehlt, wenn die Abfrage scheitert und
 * wenn die Antwort etwas anderes als `true` sagt. Wer schreiben darf, sieht
 * den Knopf, sobald die Antwort da ist.
 */

/**
 * Eine Abfrage je ApiClient - der Client entsteht einmal je Anmeldung (App.tsx),
 * die Fachseiten fragen also nicht bei jedem Seitenwechsel neu.
 */
const abfragen = new WeakMap<ApiClient, Promise<Systemrechte>>();

/**
 * Die Rechte des Angemeldeten, einmal je ApiClient geholt. Scheitert die
 * Abfrage, wird sie beim nächsten Aufruf wiederholt: Eine kurze Störung soll
 * die Knöpfe nicht bis zum Neuladen verstecken.
 */
export function rechteLaden(api: ApiClient): Promise<Systemrechte> {
  const vorhanden = abfragen.get(api);
  if (vorhanden) return vorhanden;

  // Über `then` statt direkt: Wirft der Aufruf selbst (etwa weil eine ältere
  // Attrappe die Methode nicht kennt), wird daraus eine abgelehnte Zusage und
  // kein Fehler im Rendern.
  const neu = Promise.resolve().then(() => api.systemrechte());
  abfragen.set(api, neu);
  neu.catch(() => { if (abfragen.get(api) === neu) abfragen.delete(api); });
  return neu;
}

/**
 * Ob die angemeldete Person Aufträge beauftragen darf, die ins Modell
 * schreiben. `false`, solange die Antwort fehlt, und wenn sie scheitert.
 */
export function useModellSchreibrecht(api: ApiClient): boolean {
  const [darf, setDarf] = useState(false);

  useEffect(() => {
    let aktiv = true;
    rechteLaden(api)
      .then((r) => { if (aktiv) setDarf(r?.modellSchreiben === true); })
      .catch(() => { if (aktiv) setDarf(false); });
    return () => { aktiv = false; };
  }, [api]);

  return darf;
}
